import { useMemo } from 'react';

import InputFloating from '@/components/atoms/InputFloating';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Info, Check, Ban, Database, Lock } from 'lucide-react';
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Table_NEW } from '@/components/custom/organisms/Table';

import { AirtableBase } from "@/types/crm/airtable/common";
import { airtableRequiredPermissions } from './persmissions';
import { useGetBasesForApiKey } from '@/features/crm_system/hooks/useQuery/airtable/useGetBasesForApiKey';

export type Project = {
    id: string;
    name: string;
}

type Props = {
    defaultData: {apiKey: string, baseId: string},
    onNext: (data:  {apiKey: string, bases: AirtableBase[]}) => void;
    onBack: () => void;
    onClose: () => void;
}

const Step3: React.FC<Props> = ({
    defaultData,
    onNext,
    onBack,
    onClose,
}) => {
    const schemaAutomatic = z.object({
        apiKey: z.string().min(1, 'Field is required'),
    });
    type inputFieldsAutomatic = z.infer<typeof schemaAutomatic>;
    const {
        handleSubmit: handleSubmitAutomatic,
        formState: formStateAutomatic,
        register: registerAutomatic,
        watch: watchAutomatic
    } = useForm<inputFieldsAutomatic>({ 
        resolver: zodResolver(schemaAutomatic),
        defaultValues: defaultData ? defaultData : {apiKey: ''}
    });
    const { errors: errorsAutomatic } = formStateAutomatic;
    const apiKeyWatch = watchAutomatic("apiKey");

    const { isFetching, refetch, data: basesData } = useGetBasesForApiKey(apiKeyWatch);

    // const isFetching = false;
    // const refetch= () => {};
    // const basesData = { bases: [
    //     {
    //         "id": "appj0zj0ssDZSIwUn",
    //         "name": "Collaborate Export 1",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appTdnsZqPnYsuGQ8",
    //         "name": "Test",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "apprjy3FSgRzk186V",
    //         "name": "Collaborate Export 2",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appj0zj0ssDZSIwUn",
    //         "name": "Collaborate Export 1",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appTdnsZqPnYsuGQ8",
    //         "name": "Test",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "apprjy3FSgRzk186V",
    //         "name": "Collaborate Export 2",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appj0zj0ssDZSIwUn",
    //         "name": "Collaborate Export 1",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appTdnsZqPnYsuGQ8",
    //         "name": "Test",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "apprjy3FSgRzk186V",
    //         "name": "Collaborate Export 2",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appj0zj0ssDZSIwUn",
    //         "name": "Collaborate Export 1",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "appTdnsZqPnYsuGQ8",
    //         "name": "Test",
    //         "permissionLevel": "create"
    //     },
    //     {
    //         "id": "apprjy3FSgRzk186V",
    //         "name": "Collaborate Export 2",
    //         "permissionLevel": "create"
    //     }
    // ]};

    const getBasesHandler = async () => {
        refetch();
    }

    const columnHelper = createColumnHelper<AirtableBase>();
    const tableColumns = [
        columnHelper.display({
            id: 'name',
            header: () => (
                <div className='flex items-center'>
                    <Database size="20" className="table-header__text-icon me-2"/>
                    <span className='table-header__text'>Name</span>
                </div>
            ),
            cell: (params) => (
                <div className="flex">
                    {params.row.original.name}
                </div>
            ),
        }),
        columnHelper.display({
            id: 'permission',
            header: () => (
                <div className='flex items-center'>
                    <Lock size="20" className="table-header__text-icon me-2"/>
                    <span className='table-header__text'>Permission</span>
                </div>
            ),
            cell: (params) => (
                <div className="flex">
                    { airtableRequiredPermissions.includes(params.row.original.permissionLevel) ? (
                        <div className="flex items-center gap-2 text-xs text-green-600">
                            <Check size="16" />
                            <div className="flex flex-col ">
                                <span className="font-bold">Access confirmed</span>
                                <span>You have permission to create content.</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-red-600">
                            <Ban size="16" />
                            <div className="flex flex-col ">
                                <span className="font-bold">Access restricted</span>
                                <span>You don’t have the necessary permissions.</span>
                            </div>
                        </div>
                    ) }
                </div>
            ),
        })
    ]
    const table = useReactTable({
        columns: tableColumns,
        data: useMemo(() => {
            if(basesData?.error) return [];
            return basesData?.bases || [];
        }, [basesData]),
        getCoreRowModel: getCoreRowModel(),
    })

    const onNextHandler = () => {
        if(basesData?.error || !basesData?.bases) {
            throw new Error("No bases found or error fetching bases");
        }

        onNext({
            apiKey: apiKeyWatch,
            bases: basesData?.bases?.filter((base) => airtableRequiredPermissions.includes(base.permissionLevel))
        });
    }

    const isNextButtonDisabled = () => {
        if(basesData?.error || !basesData?.bases) {
            return true;
        }

        return !(basesData?.bases?.length && apiKeyWatch?.length) || isFetching;
    }

    return (
        <>
            <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                    <InputFloating
                        label="API Key"
                        register={registerAutomatic("apiKey")}
                        errorMessage={errorsAutomatic["apiKey"]?.message}
                        showError={true}
                        type='password'
                    />
                    <div className="flex pt-[22px]">
                        <Button disabled={!apiKeyWatch?.length || isFetching} onClick={handleSubmitAutomatic(getBasesHandler)}>Fetch data</Button>
                    </div>
                </div>

                <div className="flex items-center text-blue-600">
                    <Info className="flex-shrink-0 me-1" size="16"/>
                    <p className="text-xs">If you don't see the bases you want to connect to, make sure you have the correct permissions.</p>
                </div>
        
                <Table_NEW table={table} />
            </div>

            <div className="pt-3 mt-3 border-t w-full flex items-stretch justify-between m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                
                <div className="flex m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                    <Button variant="secondary" onClick={onBack}>Back</Button>
                    <Button disabled={isNextButtonDisabled()} onClick={onNextHandler}>Next</Button>
                </div>
            </div>
        </>
    )
}

export { Step3 };
